import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import {
  planOccurrencesForRule,
  refuseDecision,
  refuseReschedule,
  type SchedulePlanRule,
} from '@erp/domain/payments';
import type {
  IRecurringPaymentRepository,
  IPaymentRepository,
  RecurringPayment,
  CreateRecurringPaymentInput,
  UpdateRecurringPaymentInput,
  RecurringPaymentFilters,
  RecurringApprovalMode,
  RecurringOccurrence,
  RecurringOccurrenceFilters,
  ApproveOccurrenceInput,
  RescheduleOccurrenceInput,
  DeclineOccurrenceInput,
  RunScheduleResult,
  GenerateDueResult,
} from '../../../interfaces/src';
import { fetchAllRows } from './fetch-all-rows';

/**
 * A `posting` claim older than this is assumed dead (the device crashed or
 * went offline mid-post) and is reclaimed by the next pass.
 */
const STALE_CLAIM_MINUTES = 10;

const OCCURRENCE_SELECT =
  '*, recurring_payments!inner(label, approval_mode, is_active)';

/** Local calendar day — NOT `toISOString()`, which rolls over at UTC midnight. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Errors thrown by the decision methods carry the domain refusal code so the
 * UI can translate them instead of showing a raw Postgres message.
 */
export class OccurrenceDecisionError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'OccurrenceDecisionError';
  }
}

export class SupabaseRecurringPaymentRepository implements IRecurringPaymentRepository {
  constructor(
    private supabase: TypedSupabaseClient,
    /**
     * Occurrences are posted THROUGH the payment repository, never with a raw
     * insert: that is what keeps the cash-drawer link, the cash movement and
     * the legal numbering identical to a hand-entered payment.
     */
    private payments: IPaymentRepository,
  ) {}

  // ==========================================================
  // Règles
  // ==========================================================

  async list(filters?: RecurringPaymentFilters): Promise<RecurringPayment[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = this.supabase.from('recurring_payments').select('*');
      if (filters?.isActive !== undefined)
        query = query.eq('is_active', filters.isActive);
      if (filters?.paymentType)
        query = query.eq('payment_type', filters.paymentType);
      if (filters?.thirdPartyType)
        query = query.eq('third_party_type', filters.thirdPartyType);
      if (filters?.search) {
        query = query.or(orContainsAnyColumn(['label'], filters.search));
      }
      return query
        .order('next_occurrence_date', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to);
    });
    return rows.map(mapRecurringPayment);
  }

  async getById(id: string): Promise<RecurringPayment | null> {
    const { data, error } = await this.supabase
      .from('recurring_payments')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapRecurringPayment(data);
  }

  async create(input: CreateRecurringPaymentInput): Promise<RecurringPayment> {
    const next = input.nextOccurrenceDate ?? input.startDate;
    const approvalMode = resolveApprovalMode(input);
    const payload = {
      label: input.label,
      payment_type: input.paymentType,
      payment_method: input.paymentMethod,
      third_party_type: input.thirdPartyType,
      third_party_id: input.thirdPartyId,
      bank_account_id: input.bankAccountId ?? null,
      amount: input.amount,
      notes: input.notes ?? null,
      frequency: input.frequency,
      interval_count: input.intervalCount ?? 1,
      day_of_month: input.dayOfMonth ?? null,
      start_date: input.startDate,
      end_date: input.endDate ?? null,
      next_occurrence_date: next,
      is_active: input.isActive ?? true,
      approval_mode: approvalMode,
      // Legacy mirror — kept in step for already-installed mobile builds.
      auto_generate: approvalMode === 'automatic',
    };

    const { data, error } = await this.supabase
      .from('recurring_payments')
      .insert(payload as never)
      .select()
      .single();
    if (error) throw error;
    return mapRecurringPayment(data);
  }

  async update(id: string, input: UpdateRecurringPaymentInput): Promise<RecurringPayment> {
    const patch: Record<string, unknown> = {};
    if (input.label !== undefined) patch['label'] = input.label;
    if (input.paymentType !== undefined) patch['payment_type'] = input.paymentType;
    if (input.paymentMethod !== undefined) patch['payment_method'] = input.paymentMethod;
    if (input.thirdPartyType !== undefined) patch['third_party_type'] = input.thirdPartyType;
    if (input.thirdPartyId !== undefined) patch['third_party_id'] = input.thirdPartyId;
    if (input.bankAccountId !== undefined) patch['bank_account_id'] = input.bankAccountId;
    if (input.amount !== undefined) patch['amount'] = input.amount;
    if (input.notes !== undefined) patch['notes'] = input.notes;
    if (input.frequency !== undefined) patch['frequency'] = input.frequency;
    if (input.intervalCount !== undefined) patch['interval_count'] = input.intervalCount;
    if (input.dayOfMonth !== undefined) patch['day_of_month'] = input.dayOfMonth;
    if (input.startDate !== undefined) patch['start_date'] = input.startDate;
    if (input.endDate !== undefined) patch['end_date'] = input.endDate;
    if (input.nextOccurrenceDate !== undefined)
      patch['next_occurrence_date'] = input.nextOccurrenceDate;
    if (input.lastGeneratedDate !== undefined)
      patch['last_generated_date'] = input.lastGeneratedDate;
    if (input.isActive !== undefined) patch['is_active'] = input.isActive;

    if (input.approvalMode !== undefined || input.autoGenerate !== undefined) {
      const approvalMode = resolveApprovalMode(input);
      patch['approval_mode'] = approvalMode;
      patch['auto_generate'] = approvalMode === 'automatic';
    }

    const { data, error } = await this.supabase
      .from('recurring_payments')
      .update(patch as never)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    // Editing a rule must be visible on the dates it has not settled yet:
    // a pending occurrence is still only a forecast. Decided and posted
    // occurrences keep their snapshot — history never rewrites itself.
    await this.syncPendingSnapshots(id, input);

    return mapRecurringPayment(data);
  }

  private async syncPendingSnapshots(
    ruleId: string,
    input: UpdateRecurringPaymentInput,
  ): Promise<void> {
    const snapshot: Record<string, unknown> = {};
    if (input.amount !== undefined) snapshot['amount'] = input.amount;
    if (input.paymentType !== undefined) snapshot['payment_type'] = input.paymentType;
    if (input.paymentMethod !== undefined) snapshot['payment_method'] = input.paymentMethod;
    if (input.thirdPartyType !== undefined) snapshot['third_party_type'] = input.thirdPartyType;
    if (input.thirdPartyId !== undefined) snapshot['third_party_id'] = input.thirdPartyId;
    if (input.bankAccountId !== undefined) snapshot['bank_account_id'] = input.bankAccountId;
    if (input.notes !== undefined) snapshot['notes'] = input.notes;
    if (Object.keys(snapshot).length === 0) return;

    const { error } = await this.supabase
      .from('recurring_payment_occurrences')
      .update(snapshot as never)
      .eq('recurring_payment_id', ruleId)
      .eq('status', 'pending');
    if (error) throw error;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase
      .from('recurring_payments')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  // ==========================================================
  // Échéances
  // ==========================================================

  async listOccurrences(filters?: RecurringOccurrenceFilters): Promise<RecurringOccurrence[]> {
    const buildQuery = () => {
      let query = this.supabase
        .from('recurring_payment_occurrences')
        .select(OCCURRENCE_SELECT);
      if (filters?.status) {
        const statuses = Array.isArray(filters.status)
          ? filters.status
          : [filters.status];
        query =
          statuses.length === 1
            ? query.eq('status', statuses[0]!)
            : query.in('status', statuses);
      }
      if (filters?.recurringPaymentId) {
        query = query.eq('recurring_payment_id', filters.recurringPaymentId);
      }
      if (filters?.dueFrom) query = query.gte('due_date', filters.dueFrom);
      if (filters?.dueTo) query = query.lte('due_date', filters.dueTo);
      return query
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });
    };

    if (filters?.limit) {
      const { data, error } = await buildQuery().limit(filters.limit);
      if (error) throw error;
      return (data ?? []).map(mapOccurrence);
    }
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      buildQuery().range(from, to),
    );
    return rows.map(mapOccurrence);
  }

  async getOccurrence(id: string): Promise<RecurringOccurrence | null> {
    const { data, error } = await this.supabase
      .from('recurring_payment_occurrences')
      .select(OCCURRENCE_SELECT)
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapOccurrence(data);
  }

  // ---- Roll-forward -----------------------------------------

  async runSchedule(today?: string): Promise<RunScheduleResult> {
    const todayDate = today ?? todayISO();

    await this.reclaimStaleClaims();

    const rules = await this.list({ isActive: true });
    const ruleById = new Map(rules.map((r) => [r.id, r]));

    // ---- 1. Matérialisation ----
    // Idempotente par construction : ON CONFLICT DO NOTHING sur
    // (recurring_payment_id, scheduled_date). Deux postes qui lancent la passe
    // en même temps écrivent le même ensemble ; le second ne crée rien.
    let materialized = 0;
    const truncatedRuleIds: string[] = [];

    for (const rule of rules) {
      const plan = planOccurrencesForRule(toPlanRule(rule), todayDate);
      if (plan.truncated) truncatedRuleIds.push(rule.id);
      if (plan.occurrences.length === 0) continue;

      const rows = plan.occurrences.map((occ) => ({
        recurring_payment_id: rule.id,
        scheduled_date: occ.scheduledDate,
        due_date: occ.scheduledDate,
        amount: rule.amount,
        payment_type: rule.paymentType,
        payment_method: rule.paymentMethod,
        third_party_type: rule.thirdPartyType,
        third_party_id: rule.thirdPartyId,
        bank_account_id: rule.bankAccountId,
        notes: rule.notes,
        status: 'pending',
      }));

      const { data: inserted, error } = await this.supabase
        .from('recurring_payment_occurrences')
        .upsert(rows as never, {
          onConflict: 'recurring_payment_id,scheduled_date',
          ignoreDuplicates: true,
        })
        .select('id');
      if (error) throw error;
      materialized += (inserted ?? []).length;

      // Le curseur avance même quand tout était déjà matérialisé : c'est ce qui
      // évite de replanifier les mêmes dates à chaque passage.
      await this.update(rule.id, {
        nextOccurrenceDate: plan.newNextOccurrenceDate,
        ...(plan.lastGeneratedDate ? { lastGeneratedDate: plan.lastGeneratedDate } : {}),
      });
    }

    // ---- 2. Post automatique ----
    // Requête indépendante du plan : elle rattrape aussi une échéance restée
    // `pending` parce qu'un post précédent a échoué, ou parce que la règle est
    // passée de « validation manuelle » à « automatique » entre-temps.
    const { data: dueRows, error: dueErr } = await this.supabase
      .from('recurring_payment_occurrences')
      .select(OCCURRENCE_SELECT)
      .eq('status', 'pending')
      .lte('due_date', todayDate)
      .eq('recurring_payments.approval_mode', 'automatic')
      .eq('recurring_payments.is_active', true)
      .order('due_date', { ascending: true });
    if (dueErr) throw dueErr;

    let autoPosted = 0;
    const failures: RunScheduleResult['failures'] = [];

    for (const row of dueRows ?? []) {
      const occurrence = mapOccurrence(row);
      const rule = ruleById.get(occurrence.recurringPaymentId);
      try {
        const posted = await this.postOccurrence(occurrence, 'automatic');
        if (posted) autoPosted += 1;
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        failures.push({
          occurrenceId: occurrence.id,
          ruleLabel: occurrence.ruleLabel ?? rule?.label ?? '',
          message,
        });
      }
    }

    const { count, error: countErr } = await this.supabase
      .from('recurring_payment_occurrences')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending');
    if (countErr) throw countErr;

    return {
      materialized,
      autoPosted,
      pending: count ?? 0,
      truncatedRuleIds,
      failures,
    };
  }

  /**
   * Un `posting` abandonné (poste éteint entre l'insert du paiement et la
   * mise à jour du statut) est réparé, pas recréé : si le paiement existe on
   * termine la transition, sinon on rend l'échéance à la file d'attente.
   */
  private async reclaimStaleClaims(): Promise<void> {
    const cutoff = new Date(Date.now() - STALE_CLAIM_MINUTES * 60_000).toISOString();
    const { data, error } = await this.supabase
      .from('recurring_payment_occurrences')
      .select('id')
      .eq('status', 'posting')
      .lt('updated_at', cutoff);
    if (error) throw error;
    if (!data?.length) return;

    for (const row of data) {
      const id = (row as { id: string }).id;
      const { data: payment } = await this.supabase
        .from('payments')
        .select('id')
        .eq('recurring_occurrence_id', id)
        .maybeSingle();

      if (payment) {
        await this.supabase
          .from('recurring_payment_occurrences')
          .update({
            status: 'posted',
            payment_id: (payment as { id: string }).id,
            last_error: null,
          } as never)
          .eq('id', id)
          .eq('status', 'posting');
      } else {
        await this.supabase
          .from('recurring_payment_occurrences')
          .update({ status: 'pending' } as never)
          .eq('id', id)
          .eq('status', 'posting');
      }
    }
  }

  /**
   * Claim atomique pending → posting. Le serveur conserve une autorisation
   * privée qui lie le token à l'instantané et aux overrides explicites : le
   * paiement générique ne peut donc pas voler une occurrence transitoire.
   */
  private async claim(
    occurrence: RecurringOccurrence,
    via: RecurringApprovalMode,
    overrides: { paymentDate?: string; amount?: number; reference?: string; note?: string },
  ): Promise<string | null> {
    const claimToken = globalThis.crypto.randomUUID();
    const args = {
      p_occurrence_id: occurrence.id,
      p_claim_token: claimToken,
      p_posted_via: via,
      p_amount: overrides.amount ?? occurrence.amount,
      p_payment_date: overrides.paymentDate ?? occurrence.dueDate,
      p_reference: overrides.reference ?? null,
      p_decision_note: overrides.note ?? null,
    };
    let result = await this.supabase.rpc(
      'claim_recurring_payment_occurrence_atomic',
      args,
    );
    if (result.error && isAmbiguousRpcFailure(result.error)) {
      // Same token and payload: a lost claim response is a safe exact replay.
      result = await this.supabase.rpc(
        'claim_recurring_payment_occurrence_atomic',
        args,
      );
    }
    if (result.error) throw result.error;
    return result.data ?? null;
  }

  /**
   * Crée le paiement d'une échéance déjà claimée (ou la claim au passage) et
   * bascule l'échéance en `posted`. Renvoie false si un autre poste l'a prise.
   */
  private async postOccurrence(
    occurrence: RecurringOccurrence,
    via: RecurringApprovalMode,
    overrides: { paymentDate?: string; amount?: number; reference?: string; note?: string } = {},
  ): Promise<boolean> {
    const claimToken = await this.claim(occurrence, via, overrides);
    if (!claimToken) return false;

    try {
      await this.payments.create({
        // One private claim is one durable posting attempt. Replaying an
        // ambiguous response uses the same token; a confirmed rejection may
        // terminally tombstone that token, then a corrected human attempt can
        // claim a fresh token without ever reusing the cancelled request key.
        clientRequestId: claimToken,
        atomicRequestKind: 'payment',
        paymentType: occurrence.paymentType,
        paymentMethod: occurrence.paymentMethod,
        thirdPartyType: occurrence.thirdPartyType,
        thirdPartyId: occurrence.thirdPartyId ?? '',
        ...(occurrence.bankAccountId ? { bankAccountId: occurrence.bankAccountId } : {}),
        amount: overrides.amount ?? occurrence.amount,
        paymentDate: overrides.paymentDate ?? occurrence.dueDate,
        ...(overrides.reference ? { reference: overrides.reference } : {}),
        notes: occurrence.notes ?? occurrence.ruleLabel ?? undefined,
        recurringPaymentId: occurrence.recurringPaymentId,
        recurringOccurrenceId: occurrence.id,
        recurringClaimToken: claimToken,
      });
      return true;
    } catch (e) {
      // The release command changes state only while the exact private claim is
      // still present and no payment committed. A post-commit read/network
      // failure therefore cannot reopen an already-posted occurrence.
      const message = e instanceof Error ? e.message : String(e);
      await this.supabase.rpc('release_recurring_payment_occurrence_claim', {
        p_occurrence_id: occurrence.id,
        p_claim_token: claimToken,
        p_last_error: message.slice(0, 500),
      });
      throw e;
    }
  }

  // ---- Décisions humaines ------------------------------------

  async approveOccurrence(
    id: string,
    input: ApproveOccurrenceInput = {},
  ): Promise<RecurringOccurrence> {
    const occurrence = await this.requireOccurrence(id);
    const refusal = refuseDecision(occurrence, 'approve');
    if (refusal) throw new OccurrenceDecisionError(refusal);

    const posted = await this.postOccurrence(occurrence, 'manual', {
      ...(input.paymentDate ? { paymentDate: input.paymentDate } : {}),
      ...(input.amount !== undefined ? { amount: input.amount } : {}),
      ...(input.reference ? { reference: input.reference } : {}),
      ...(input.note ? { note: input.note } : {}),
    });
    if (!posted) throw new OccurrenceDecisionError('occurrence.posting');

    return this.requireOccurrence(id);
  }

  async rescheduleOccurrence(
    id: string,
    input: RescheduleOccurrenceInput,
  ): Promise<RecurringOccurrence> {
    const occurrence = await this.requireOccurrence(id);
    const refusal = refuseReschedule(occurrence, input.dueDate, todayISO());
    if (refusal) throw new OccurrenceDecisionError(refusal);

    return this.applyDecision(id, {
      due_date: input.dueDate,
      // Garde la date d'origine : un second report ne l'écrase pas.
      rescheduled_from: occurrence.rescheduledFrom ?? occurrence.dueDate,
      ...(input.note ? { decision_note: input.note } : {}),
    });
  }

  async skipOccurrence(
    id: string,
    input: DeclineOccurrenceInput = {},
  ): Promise<RecurringOccurrence> {
    return this.decline(id, 'skip', 'skipped', input);
  }

  async rejectOccurrence(
    id: string,
    input: DeclineOccurrenceInput = {},
  ): Promise<RecurringOccurrence> {
    return this.decline(id, 'reject', 'rejected', input);
  }

  private async decline(
    id: string,
    decision: 'skip' | 'reject',
    status: 'skipped' | 'rejected',
    input: DeclineOccurrenceInput,
  ): Promise<RecurringOccurrence> {
    const occurrence = await this.requireOccurrence(id);
    const refusal = refuseDecision(occurrence, decision);
    if (refusal) throw new OccurrenceDecisionError(refusal);

    return this.applyDecision(id, {
      status,
      decided_at: new Date().toISOString(),
      decided_by: await this.currentUserId(),
      decision_note: input.note ?? null,
      last_error: null,
    });
  }

  async resetOccurrence(id: string): Promise<RecurringOccurrence> {
    const occurrence = await this.requireOccurrence(id);
    const refusal = refuseDecision(occurrence, 'reset');
    if (refusal) throw new OccurrenceDecisionError(refusal);

    return this.applyDecision(id, {
      status: 'pending',
      decided_at: null,
      decided_by: null,
      decision_note: null,
      last_error: null,
    });
  }

  /**
   * Écrit une décision en n'acceptant QUE le statut lu juste avant : deux
   * utilisateurs qui décident la même échéance à la seconde près ne se
   * marchent pas dessus, le second reçoit `occurrence.alreadyDecided`.
   */
  private async applyDecision(
    id: string,
    patch: Record<string, unknown>,
  ): Promise<RecurringOccurrence> {
    const { data, error } = await this.supabase
      .from('recurring_payment_occurrences')
      .update(patch as never)
      .eq('id', id)
      .in('status', patch['status'] === 'pending' ? ['skipped', 'rejected'] : ['pending'])
      .select(OCCURRENCE_SELECT)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new OccurrenceDecisionError('occurrence.alreadyDecided');
    return mapOccurrence(data);
  }

  private async requireOccurrence(id: string): Promise<RecurringOccurrence> {
    const occurrence = await this.getOccurrence(id);
    if (!occurrence) throw new OccurrenceDecisionError('occurrence.notFound');
    return occurrence;
  }

  /** Read from the cached session — no network round trip. */
  private async currentUserId(): Promise<string | null> {
    const { data } = await this.supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  }

  // ==========================================================
  // Compatibilité
  // ==========================================================

  /** @deprecated see IRecurringPaymentRepository.generateDue */
  async generateDue(today?: string): Promise<GenerateDueResult> {
    const result = await this.runSchedule(today);
    return {
      generated: result.autoPosted,
      rulesProcessed: result.materialized,
    };
  }
}

function resolveApprovalMode(input: {
  approvalMode?: RecurringApprovalMode;
  autoGenerate?: boolean;
}): RecurringApprovalMode {
  if (input.approvalMode) return input.approvalMode;
  if (input.autoGenerate !== undefined) return input.autoGenerate ? 'automatic' : 'manual';
  // Nouvelle règle sans précision : rien n'entre en compta sans un feu vert.
  return 'manual';
}

function toPlanRule(rule: RecurringPayment): SchedulePlanRule {
  return {
    id: rule.id,
    isActive: rule.isActive,
    approvalMode: rule.approvalMode,
    frequency: rule.frequency,
    interval: rule.intervalCount,
    dayOfMonth: rule.dayOfMonth,
    startDate: rule.startDate,
    endDate: rule.endDate,
    nextOccurrenceDate: rule.nextOccurrenceDate,
  };
}

function isAmbiguousRpcFailure(error: { code?: string | null }): boolean {
  return typeof error.code !== 'string' || !/^[0-9A-Z]{5}$/.test(error.code);
}

function mapRecurringPayment(row: Record<string, unknown>): RecurringPayment {
  const autoGenerate = (row['auto_generate'] as boolean) ?? true;
  const approvalMode =
    (row['approval_mode'] as RecurringApprovalMode | null) ??
    (autoGenerate ? 'automatic' : 'manual');
  return {
    id: row['id'] as string,
    label: row['label'] as string,
    paymentType: row['payment_type'] as RecurringPayment['paymentType'],
    paymentMethod: row['payment_method'] as RecurringPayment['paymentMethod'],
    thirdPartyType: row['third_party_type'] as RecurringPayment['thirdPartyType'],
    thirdPartyId: row['third_party_id'] as string,
    bankAccountId: (row['bank_account_id'] as string) ?? null,
    amount: Number(row['amount']),
    notes: (row['notes'] as string) ?? null,
    frequency: row['frequency'] as RecurringPayment['frequency'],
    intervalCount: (row['interval_count'] as number) ?? 1,
    dayOfMonth: (row['day_of_month'] as number) ?? null,
    startDate: row['start_date'] as string,
    endDate: (row['end_date'] as string) ?? null,
    nextOccurrenceDate: row['next_occurrence_date'] as string,
    lastGeneratedDate: (row['last_generated_date'] as string) ?? null,
    isActive: (row['is_active'] as boolean) ?? true,
    autoGenerate,
    approvalMode,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapOccurrence(row: Record<string, unknown>): RecurringOccurrence {
  const rule = row['recurring_payments'] as
    | { label?: string; approval_mode?: RecurringApprovalMode }
    | null
    | undefined;
  return {
    id: row['id'] as string,
    recurringPaymentId: row['recurring_payment_id'] as string,
    scheduledDate: row['scheduled_date'] as string,
    dueDate: row['due_date'] as string,
    rescheduledFrom: (row['rescheduled_from'] as string) ?? null,
    amount: Number(row['amount']),
    paymentType: row['payment_type'] as RecurringOccurrence['paymentType'],
    paymentMethod: row['payment_method'] as RecurringOccurrence['paymentMethod'],
    thirdPartyType: row['third_party_type'] as RecurringOccurrence['thirdPartyType'],
    thirdPartyId: (row['third_party_id'] as string) ?? null,
    bankAccountId: (row['bank_account_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    status: row['status'] as RecurringOccurrence['status'],
    postedVia: (row['posted_via'] as RecurringApprovalMode) ?? null,
    paymentId: (row['payment_id'] as string) ?? null,
    decisionNote: (row['decision_note'] as string) ?? null,
    decidedAt: (row['decided_at'] as string) ?? null,
    decidedBy: (row['decided_by'] as string) ?? null,
    lastError: (row['last_error'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    ruleLabel: rule?.label ?? null,
    ruleApprovalMode: rule?.approval_mode ?? null,
  };
}
